import React from 'react';
import {
  MdOutlineAccountBalance, MdAssignment, MdWarning, MdCalendarMonth,
  MdDescription, MdCampaign, MdInbox, MdBarChart, MdBusiness, MdAccountBalance,
  MdGroup, MdBadge,
} from 'react-icons/md';
import NotificheBadgeIcon from '../components/NotificheBadgeIcon';
import NotificheOperatoreBadgeIcon from '../../operatore/components/NotificheOperatoreBadgeIcon';

/**
 * Costruisce i gruppi di link della sidebar PPP in base al ruolo dell'utente.
 *
 * Ruoli attivi:
 *  - operatore_economico  → solo portale operatore
 *  - amministratore_ente  → solo gestione ente e membri (niente procedimenti)
 *  - rup                  → tutto tranne Reportistica e Enti PA; "I miei enti" solo suo
 *  - dirigente            → tutto incluso Reportistica; NON vede "I miei enti"
 *
 * @param {object} user  shape utente da useAuthUser (con .role)
 * @param {(perm: string) => boolean} can  helper permessi
 * @returns {Array<{title: string, links: Array}>} gruppi sidebar per setExtraLinks
 */
export function buildSidebarLinks(user, can) {
  if (user?.role === 'operatore_economico') {
    return [{
      title: 'Portale Operatore',
      links: [
        { name: 'operatore/avvisi', label: 'Avvisi pubblici', icon: <MdCampaign />, end: false },
        { name: 'operatore/proposte', label: 'Le mie proposte', icon: <MdInbox />, end: false },
        { name: 'operatore/notifiche', label: 'Notifiche', icon: <NotificheOperatoreBadgeIcon /> },
        { name: 'operatore/company', title: 'op_organizzazione', icon: <MdBusiness />, end: false },
      ],
    }];
  }

  if (user?.role === 'amministratore_ente') {
    return [
      {
        title: 'Il mio ente',
        links: [
          { name: 'pa/ente', label: 'Il mio ente', icon: <MdAccountBalance />, end: true },
          { name: 'pa/membri', label: 'Membri ente', icon: <MdGroup />, end: true },
          { name: 'pa/rup-associati', label: "RUP dell'ente", icon: <MdBadge />, end: true },
        ],
      },
    ];
  }

  const isRup = user?.role === 'rup';
  const isDirigente = user?.role === 'dirigente';
  const isUfficioTecnico = user?.role === 'ufficio_tecnico';

  // Ordine: segue il flusso procedurale PPP (F01 → F04, poi viste operative).
  const links = [
    // Dirigente e ufficio tecnico vedono il proprio ente nella sidebar
    (isDirigente || isUfficioTecnico) && {
      name: 'pa/ente', label: 'Il mio ente', icon: <MdAccountBalance />, end: true,
    },
    // Dirigente può gestire membri e RUP del proprio ente
    isDirigente && can('ppp.membri.view') && {
      name: 'pa/membri', label: 'Membri ente', icon: <MdGroup />, end: true,
    },
    isDirigente && can('ppp.ente.invite_rup') && {
      name: 'pa/rup-associati', label: "RUP dell'ente", icon: <MdBadge />, end: true,
    },
    can('ppp.programmazione.view') && {
      name: 'ppp/programmazione', title: 'ppp_programmazione_sidebar', icon: <MdCalendarMonth />, end: false,
    },
    can('ppp.dip.view') && {
      name: 'ppp/dip', title: 'ppp_dip_sidebar', icon: <MdDescription />, end: false,
    },
    can('ppp.avviso.view') && {
      name: 'ppp/avvisi-pubblici', title: 'ppp_avviso_sidebar', icon: <MdCampaign />, end: false,
    },
    can('ppp.proposte.view') && {
      name: 'ppp/proposte', title: 'ppp_proposte_sidebar', icon: <MdInbox />, end: false,
    },
    // Dirigente vede tutti i procedimenti del proprio ente; RUP solo i propri assegnati
    isDirigente && can('ppp.view') && {
      name: 'ppp', title: 'ppp_procedimenti', icon: <MdOutlineAccountBalance />, end: false,
    },
    isRup && can('ppp.view') && {
      name: 'ppp/assegnati', title: 'ppp_assegnati_sidebar', icon: <MdAssignment />,
    },
    // "I miei enti" — solo RUP: vista degli enti che lo hanno invitato
    isRup && {
      name: 'pa/miei-enti', label: 'I miei enti', icon: <MdAccountBalance />,
    },
    can('ppp.scadenze.view') && {
      name: 'ppp/report-ritardi', title: 'ppp_report_ritardi_sidebar', icon: <MdWarning />,
    },
    // Reportistica — solo Dirigente
    isDirigente && can('ppp.report.view') && {
      name: 'ppp/report', title: 'ppp_reportistica_sidebar', icon: <MdBarChart />, end: false,
    },
    can('ppp.notifiche.view') && {
      name: 'ppp/notifiche', title: 'ppp_notifiche_titolo', icon: <NotificheBadgeIcon />,
    },
  ].filter(Boolean);

  return links.length > 0 ? [{ title: 'PPP', links }] : [];
}
